import { ChevronDownIcon } from '@/assets/icons/chevron-down.icon';
import { ChevronLeftIcon } from '@/assets/icons/chevron-left.icon';
import { ChevronRightIcon } from '@/assets/icons/chevron-right.icon';
import { cn } from '@/core/utils/cn';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';

const LIMIT_OPTIONS = [10, 20, 50, 100];

interface ICardMetricsPaginationProps {
  page: number;
  totalPages: number;
  limit: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

const CardMetricsPagination = ({ page, totalPages, limit, setPage, setLimit }: ICardMetricsPaginationProps) => {
  const goToPrev = () => {
    if (page > 1) setPage(page - 1);
  };

  const goToNext = () => {
    if (page < totalPages) setPage(page + 1);
  };

  return (
    <nav className="flex items-center justify-between border-t border-secondary py-4">
      <div className="flex items-center">
        <button
          className={cn(
            'inline-flex items-center pr-1 text-sm font-medium text-light hover:text-lighter',
            page <= 1 ? 'opacity-50 hover:text-light' : '',
          )}
          onClick={goToPrev}
        >
          <ChevronLeftIcon className="mr-0 md:mr-1 h-5 w-5 text-[#525760]" aria-hidden="true" />
          Prev
        </button>
        <span className="inline-flex items-center px-2 md:px-4 text-sm font-medium text-lighter hover:text-lighter">
          {page}
        </span>
        <button
          className={cn(
            'inline-flex items-center pl-1 text-sm font-medium text-light hover:text-lighter',
            page >= totalPages ? 'opacity-50 hover:text-light' : '',
          )}
          onClick={goToNext}
        >
          Next
          <ChevronRightIcon className="ml-0 md:ml-1 h-5 w-5 text-[#525760]" aria-hidden="true" />
        </button>
      </div>

      <div className="flex items-center gap-x-2">
        <Listbox value={limit} onChange={setLimit}>
          <ListboxButton
            className={cn(
              'text-lighter relative block min-w-12 md:min-w-16 rounded-lg bg-surface-dark border border-secondary py-1.5 pr-8 pl-2 text-left text-sm/6',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25',
            )}
          >
            {limit}
            <ChevronDownIcon className="group pointer-events-none absolute top-2.5 right-2.5 size-4" />
          </ListboxButton>
          <ListboxOptions
            anchor="bottom"
            transition
            className={cn(
              'z-10 w-[var(--button-width)] rounded-lg border border-secondary bg-surface-dark p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none',
              'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
            )}
          >
            {LIMIT_OPTIONS.map((limit: number) => (
              <ListboxOption
                key={limit}
                value={limit}
                className=" group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-dark"
              >
                <div className="text-sm/6 text-lighter">{limit}</div>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>

        <div className="text-lighter text-xs md:text-sm">entries per page</div>
      </div>
    </nav>
  );
};

export default CardMetricsPagination;
